(() => {
 'use strict';
 // Copy highlighting is optional and loads only when explicitly requested for review.
 if (new URLSearchParams(location.search).get('changes') === '1' && document.currentScript?.src) {
  const reviewScript = document.createElement('script');
  reviewScript.src = new URL('pm-sp-copy-review.js', document.currentScript.src).href;
  reviewScript.defer = true;
  document.head.append(reviewScript);
 }
 const controllers = new WeakMap();
 function initialize(page) {
  controllers.get(page)?.();
  const cleanups = [];
  const controller = new AbortController();
  const signal = controller.signal;
  const designMode = Boolean(window.Shopify?.designMode);
  // External editorial links open separately; storefront navigation stays in this tab.
  const storefrontHosts = new Set([location.hostname, 'paolomoschino.com', 'www.paolomoschino.com', window.Shopify?.shop].filter(Boolean));
  page.querySelectorAll('a[href]').forEach(link => {
   const destination = new URL(link.getAttribute('href'), location.href);
   if (!['http:', 'https:'].includes(destination.protocol) || storefrontHosts.has(destination.hostname)) return;
   link.target = '_blank';
   link.relList.add('noopener', 'noreferrer');
  });
  const openingHero = page.querySelector('.pmsp-hero');
  const updateHeader = () => {
   const bounds = openingHero?.getBoundingClientRect();
   const hidden = !designMode && Boolean(bounds && bounds.top >= -40 && bounds.bottom > 0);
   document.documentElement.classList.toggle('pmsp-header-hidden', hidden);
   const header = document.querySelector('.shopify-section--header');
   if (header) header.inert = hidden;
  };
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true, signal });
  window.addEventListener('pageshow', updateHeader, { signal });
  cleanups.push(() => {
   document.documentElement.classList.remove('pmsp-header-hidden');
   const header = document.querySelector('.shopify-section--header');
   if (header) header.inert = false;
  });
  const fitFilmHeading = () => {
   if (signal.aborted) return;
   page.querySelectorAll('.reel-statement').forEach(heading => {
    const first = heading.querySelector('span');
    const second = heading.querySelector('em');
    if (!first || !second) return;
    const measure = element => {
     const range = document.createRange();
     range.selectNodeContents(element);
     return range.getBoundingClientRect().width;
    };
    const firstWidth = measure(first);
    const secondWidth = measure(second);
    if (firstWidth && secondWidth) {
     const size = parseFloat(getComputedStyle(second).fontSize) * firstWidth / secondWidth;
     heading.style.setProperty('--reel-second-size', `${size}px`);
    }
   });
  };
  document.fonts.ready.then(fitFilmHeading);
  window.addEventListener('resize', fitFilmHeading, { passive: true, signal });
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  document.documentElement.classList.add('motion-ready');
  document.documentElement.classList.toggle('pmsp-editor', designMode);
  const observe = (callback, options) => {
   const observer = new IntersectionObserver(callback, options);
   cleanups.push(() => observer.disconnect());
   return observer;
  };
let refreshGrain = () => {};
const pageLoader = page.querySelector('.page-loader');
if (pageLoader) {
 const root = document.documentElement;
 const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
 let fallback;
 let handoff;
 let videoCue;
 let heroVideo;
 let videoPlaceholder;
 let videoStyle;
 let heroContent;
 let contentPlaceholder;
 let contentStyle;
 let refreshLoaderText;
 const titleAnimations = [];
 const eyebrowPairs = [];
 let loaderFinished = false;
 const preventScroll = event => { if (event.cancelable) event.preventDefault(); };
 const preventScrollKeys = event => {
  if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) preventScroll(event);
 };
 const finishLoader = () => {
  if (loaderFinished) return;
  loaderFinished = true;
  clearTimeout(fallback);
  clearTimeout(handoff);
  clearTimeout(videoCue);
  if (heroVideo && videoPlaceholder?.parentNode) {
   videoPlaceholder.replaceWith(heroVideo);
   heroVideo.classList.remove('loader-video', 'is-showing');
   if (videoStyle === null) heroVideo.removeAttribute('style');
   else heroVideo.setAttribute('style', videoStyle);
   refreshGrain();
  }
  if (heroContent && contentPlaceholder?.parentNode) {
   contentPlaceholder.replaceWith(heroContent);
   heroContent.removeEventListener('click', preventScroll);
   if (contentStyle === null) heroContent.removeAttribute('style');
   else heroContent.setAttribute('style', contentStyle);
  }
  for (const animation of titleAnimations) animation.cancel();
  window.removeEventListener('resize', finishLoader);
  root.classList.remove('loader-running', 'loader-text-ready', 'pmsp-scroll-locked');
  root.classList.add('loader-complete');
  pageLoader.remove();
  document.removeEventListener('keydown', preventScrollKeys);
  document.removeEventListener('wheel', preventScroll);
  document.removeEventListener('touchmove', preventScroll);
  motionPreference.removeEventListener('change', finishLoader);
 };
 cleanups.push(finishLoader);
 // Deep links and restored scroll positions should open directly at their content.
 const hasContentAnchor = Boolean(location.hash && !['#', '#top'].includes(location.hash));
 if (motionPreference.matches || designMode || window.scrollY > 2 || hasContentAnchor) finishLoader();
 else {
  // Arm the fallback before starting; a missing animation must never hide the hero.
  fallback = setTimeout(finishLoader, 4300);
  for (const [selector, className] of [
   ['.hero-title-pm .hero-kicker', 'page-loader-eyebrow-pm'],
   ['.hero-title-sp .hero-kicker', 'page-loader-eyebrow-sp']
  ]) {
   const target = page.querySelector(selector);
   if (!target) continue;
   const eyebrow = document.createElement('span');
   eyebrow.className = `page-loader-eyebrow ${className}`;
   eyebrow.textContent = target.textContent;
   eyebrow.setAttribute('aria-hidden', 'true');
   pageLoader.append(eyebrow);
   eyebrowPairs.push([eyebrow, target]);
  }
  heroContent = page.querySelector('.hero-content-row');
  if (heroContent) {
   contentStyle = heroContent.getAttribute('style');
   contentPlaceholder = document.createComment('Hero supporting copy returns here after the loader');
   heroContent.before(contentPlaceholder);
   heroContent.addEventListener('click', preventScroll);
  }
  // Read the final layout in its original context; the loader uses exactly the same geometry.
  refreshLoaderText = () => {
   if (!pageLoader.isConnected) return;
   if (heroContent && contentPlaceholder?.parentNode) {
    contentPlaceholder.after(heroContent);
    if (contentStyle === null) heroContent.removeAttribute('style');
    else heroContent.setAttribute('style', contentStyle);
    const bounds = heroContent.getBoundingClientRect();
    const styles = getComputedStyle(heroContent);
    const layout = {};
    for (const property of ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'gridTemplateColumns', 'gridTemplateRows', 'columnGap', 'rowGap', 'alignItems']) layout[property] = styles[property];
    pageLoader.append(heroContent);
    Object.assign(heroContent.style, layout, {
     left: `${bounds.left}px`, top: `${bounds.top}px`,
     width: `${bounds.width}px`, height: `${bounds.height}px`
    });
   }
   const textPairs = [
    [pageLoader.querySelector('.page-loader-name-pm'), page.querySelector('.hero-title-pm h1')],
    [pageLoader.querySelector('.page-loader-name-sp'), page.querySelector('.hero-title-sp p')],
    ...eyebrowPairs
   ];
   for (const [name, target] of textPairs) {
    if (!name || !target) continue;
    const bounds = target.getBoundingClientRect();
    const styles = getComputedStyle(target);
    Object.assign(name.style, {
     left: `${bounds.left}px`, top: `${bounds.top}px`, width: `${bounds.width}px`, height: `${bounds.height}px`,
     fontFamily: styles.fontFamily, fontSize: styles.fontSize, fontWeight: styles.fontWeight,
     fontStyle: styles.fontStyle, lineHeight: styles.lineHeight, letterSpacing: styles.letterSpacing,
     textTransform: styles.textTransform, textAlign: styles.textAlign
    });
   }
  };
  const positionGallery = () => {
   if (loaderFinished) return;
   const card = pageLoader.querySelector('.page-loader-card');
   const pm = pageLoader.querySelector('.page-loader-name-pm');
   const sp = pageLoader.querySelector('.page-loader-name-sp');
   if (!card || !pm || !sp) return;
   const heroBounds = openingHero.getBoundingClientRect();
   const overlayBounds = pageLoader.getBoundingClientRect();
   const clearance = window.innerWidth < 700 ? 16 : 28;
   const desiredWidth = Math.min(window.innerWidth * (window.innerWidth < 700 ? .78 : .46), 680);
   const centerX = heroBounds.left + heroBounds.width / 2;
   const left = centerX - desiredWidth / 2;
   const right = centerX + desiredWidth / 2;
   // Measure actual text runs: short right-aligned lines need not shrink the gallery.
   const textBounds = selector => [...page.querySelectorAll(selector)].map(element => {
    const range = document.createRange();
    range.selectNodeContents(element);
    return range.getBoundingClientRect();
   }).filter(bounds => bounds.right > left - clearance && bounds.left < right + clearance);
   const upper = textBounds('.hero-title-pm .hero-line-inner, .hero-title-pm .hero-kicker');
   const lower = textBounds('.hero-title-sp .hero-line-inner, .hero-title-sp .hero-kicker');
   const top = Math.max(heroBounds.top + clearance, ...upper.map(bounds => bounds.bottom + clearance));
   const bottom = Math.min(heroBounds.bottom - clearance, ...lower.map(bounds => bounds.top - clearance));
   const centerY = (top + bottom) / 2;
   const baseHeight = Math.min(desiredWidth / 2.4, Math.max(0, bottom - top) * .9);
   const width = baseHeight * 2.4;
   const height = baseHeight * 3;
   Object.assign(card.style, {
    width: `${width}px`, height: `${height}px`,
    left: `${heroBounds.left + heroBounds.width / 2 - overlayBounds.left - width / 2}px`,
    top: `${centerY - height / 2 - overlayBounds.top}px`
   });
  };
  root.classList.add('loader-running', 'pmsp-scroll-locked');
  refreshLoaderText();
  positionGallery();
  document.fonts.ready.then(() => {
   if (loaderFinished) return;
   refreshLoaderText();
   positionGallery();
   root.classList.add('loader-text-ready');
  });
  // Use the actual hero film as the last gallery frame, preserving playback through the handoff.
  heroVideo = page.querySelector('.hero-stage .hero-backdrop');
  const galleryWindow = pageLoader.querySelector('.page-loader-window');
  if (heroVideo && galleryWindow && typeof heroVideo.animate === 'function') {
   const heroBounds = heroVideo.getBoundingClientRect();
   videoStyle = heroVideo.getAttribute('style');
   videoPlaceholder = document.createComment('Hero video returns here after the loader');
   heroVideo.before(videoPlaceholder);
   heroVideo.classList.add('loader-video');
   Object.assign(heroVideo.style, {
    left: `${heroBounds.left}px`, top: `${heroBounds.top}px`,
    width: `${heroBounds.width}px`, height: `${heroBounds.height}px`
   });
   const heroShade = page.querySelector('.hero-stage .hero-shade');
   const shade = document.createElement('div');
   shade.className = 'loader-video-shade';
   if (heroShade) shade.style.background = getComputedStyle(heroShade).background;
   Object.assign(shade.style, {
    left: `${heroBounds.left}px`, top: `${heroBounds.top}px`,
    width: `${heroBounds.width}px`, height: `${heroBounds.height}px`
   });
   const paper = document.createElement('div');
   paper.className = 'loader-video-paper';
   Object.assign(paper.style, {
    left: `${heroBounds.left}px`, top: `${heroBounds.top}px`,
    width: `${heroBounds.width}px`, height: `${heroBounds.height}px`, display: 'none'
   });
   pageLoader.append(heroVideo, shade, paper);
   videoCue = setTimeout(() => {
    const frame = galleryWindow.getBoundingClientRect();
    const crop = `inset(${frame.top - heroBounds.top}px ${heroBounds.right - frame.right}px ${heroBounds.bottom - frame.bottom}px ${frame.left - heroBounds.left}px)`;
    const overlayBounds = pageLoader.getBoundingClientRect();
    const finalOverlayCrop = `inset(${Math.max(0, heroBounds.top - overlayBounds.top)}px ${Math.max(0, overlayBounds.right - heroBounds.right)}px ${Math.max(0, overlayBounds.bottom - heroBounds.bottom)}px ${Math.max(0, heroBounds.left - overlayBounds.left)}px)`;
    titleAnimations.push(pageLoader.animate([
     { clipPath: 'inset(0px)', offset: 0 },
     { clipPath: 'inset(0px)', offset: 450 / 1450, easing: 'cubic-bezier(.2,0,0,1)' },
     { clipPath: finalOverlayCrop, offset: 1 }
    ], { duration: 1450, fill: 'both' }));
    heroVideo.classList.add('is-showing');
    paper.style.display = 'block';
    titleAnimations.push(paper.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 1000, delay: 450, easing: 'cubic-bezier(.2,0,0,1)', fill: 'both' }));
    for (const layer of [heroVideo, shade, paper]) {
     titleAnimations.push(layer.animate([
      { clipPath: crop, offset: 0 },
      { clipPath: crop, offset: 450 / 1450, easing: 'cubic-bezier(.2,0,0,1)' },
      { clipPath: 'inset(0px)', offset: 1 }
     ], { duration: 1450, fill: 'both' }));
    }
    titleAnimations.push(shade.animate([{ opacity: 0 }, { opacity: 1 }], {
     duration: 1000, delay: 450, easing: 'cubic-bezier(.2,0,0,1)', fill: 'both'
    }));
   }, 2450);
  } else { finishLoader(); }
  // Text stays fixed from the first frame; only its colour changes during video expansion.
  handoff = setTimeout(() => {
   refreshLoaderText();
   for (const element of [heroContent, ...pageLoader.querySelectorAll('.page-loader-name, .page-loader-eyebrow')].filter(Boolean)) {
    if (typeof element.animate !== 'function') continue;
    titleAnimations.push(element.animate([{ color: getComputedStyle(page).getPropertyValue('--ink').trim() }, { color: '#ffffff' }], {
     duration: 1000, easing: 'cubic-bezier(.2,0,0,1)', fill: 'forwards'
    }));
   }
   Promise.all(titleAnimations.map(animation => animation.finished)).then(finishLoader).catch(() => {});
  }, 2900);
  window.addEventListener('resize', finishLoader);
  document.addEventListener('keydown', preventScrollKeys);
  document.addEventListener('wheel', preventScroll, { passive: false });
  document.addEventListener('touchmove', preventScroll, { passive: false });
  motionPreference.addEventListener('change', finishLoader);
  window.addEventListener('pagehide', finishLoader, { once: true });
  window.addEventListener('pageshow', () => { if (window.scrollY > 2) finishLoader(); }, { signal });
 }
} else document.documentElement.classList.add('loader-complete');

  for (const section of page.querySelectorAll('.ring-section')) {
   const tabs = [...section.querySelectorAll('[role=tab]')];
   function selectTab(tab, focus = false) {
    for (const item of tabs) {
     const active = item === tab;
     item.setAttribute('aria-selected', String(active));
     item.tabIndex = active ? 0 : -1;
     const panel = section.querySelector(`[id="${item.dataset.panel}"]`);
     if (panel) panel.hidden = !active;
    }
    if (focus) tab.focus();
   }
   for (const tab of tabs) {
    tab.addEventListener('click', () => selectTab(tab), { signal });
    tab.addEventListener('keydown', event => {
     const index = tabs.indexOf(tab);
     const target = { ArrowRight: tabs[(index + 1) % tabs.length], ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length], Home: tabs[0], End: tabs.at(-1) }[event.key];
     if (target) { event.preventDefault(); selectTab(target, true); }
    }, { signal });
   }
  }
  // Measure actual row and copy heights so the two reading gaps stay identical.
  for (const section of page.querySelectorAll('.pmsp-collections')) {
   const orbit = section.querySelector('.product-orbit');
   const copy = section.querySelector('.ring-copy');
   if (!orbit || !copy) continue;
   const balanceCollection = () => {
    if (signal.aborted || !section.isConnected) return;
    const panel = section.querySelector('.ring-panel:not([hidden])');
    const rows = panel?.querySelectorAll('.sample-row');
    if (!panel || rows?.length !== 2) return;
    const gap = parseFloat(getComputedStyle(section).getPropertyValue('--collection-intro-gap')) || 64;
    const panelStyle = getComputedStyle(panel);
    const topPadding = parseFloat(panelStyle.paddingTop) || 0;
    const bottomPadding = parseFloat(panelStyle.paddingBottom) || 0;
    const top = topPadding + rows[0].getBoundingClientRect().height + gap;
    const height = top + copy.getBoundingClientRect().height + gap + rows[1].getBoundingClientRect().height + bottomPadding;
    orbit.style.setProperty('--collection-copy-top', `${top}px`);
    orbit.style.setProperty('--collection-orbit-height', `${height}px`);
    orbit.classList.add('has-balanced-collection');
   };
   balanceCollection();
   document.fonts?.ready.then(balanceCollection);
   window.addEventListener('resize', balanceCollection, { passive: true, signal });
   section.addEventListener('click', () => requestAnimationFrame(balanceCollection), { signal });
   if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(balanceCollection);
    observer.observe(copy);
    section.querySelectorAll('.sample-row').forEach(row => observer.observe(row));
    cleanups.push(() => observer.disconnect());
   }
   cleanups.push(() => {
    orbit.classList.remove('has-balanced-collection');
    orbit.style.removeProperty('--collection-copy-top');
    orbit.style.removeProperty('--collection-orbit-height');
   });
  }
  const videos = [...page.querySelectorAll('video.ambient-video')].filter(video => !video.closest('[aria-hidden="true"]'));
  const videoVisibility = new WeakMap();
  const bufferingFilms = new WeakSet();
  // The opening loader temporarily borrows one hero video before returning it.
  const heroFilms = videos.filter(video => video.closest('.pmsp-hero,.page-loader'));
  const filmHeroes = [...page.querySelectorAll('.pmsp-hero')];
  function refreshHeroGrain() {
   for (const hero of filmHeroes) {
    const playing = !reducedMotion.matches && !designMode && !document.hidden && heroFilms.some(video =>
     video.closest('.pmsp-hero') === hero && videoVisibility.get(video) &&
     !video.paused && !video.ended && !bufferingFilms.has(video) && video.readyState >= 3
    );
    hero.classList.toggle('has-playing-film', playing);
   }
  }
  refreshGrain = refreshHeroGrain;
  for (const video of heroFilms) {
   for (const event of ['playing', 'pause', 'waiting', 'ended', 'emptied']) {
    video.addEventListener(event, () => {
     if (event === 'waiting' || event === 'emptied') bufferingFilms.add(video);
     if (event === 'playing') bufferingFilms.delete(video);
     refreshHeroGrain();
    }, { signal });
   }
  }
  cleanups.push(() => filmHeroes.forEach(hero => hero.classList.remove('has-playing-film')));
  function playVideo(video, visible) {
   if (reducedMotion.matches || designMode || !visible || document.hidden) { video.pause(); return; }
   video.play().catch(() => {});
  }
  const videoObserver = observe(entries => {
   for (const entry of entries) {
    videoVisibility.set(entry.target, entry.isIntersecting);
    playVideo(entry.target, entry.isIntersecting);
    refreshHeroGrain();
   }
  }, { threshold: .18 });
  for (const video of videos) videoObserver.observe(video);
  cleanups.push(() => videos.forEach(video => video.pause()));
  for (const gallery of page.querySelectorAll('.purpose-gallery')) {
   if (gallery.querySelector('.purpose-track') || designMode) continue;
   const track = document.createElement('div'); track.className = 'purpose-track';
   const primary = document.createElement('div'); primary.className = 'purpose-set';
   primary.append(...gallery.children);
   const duplicate = primary.cloneNode(true); duplicate.setAttribute('aria-hidden', 'true'); duplicate.inert = true;
   for (const image of duplicate.querySelectorAll('img')) image.alt = '';
   for (const element of duplicate.querySelectorAll('[data-shopify-editor-block]')) element.removeAttribute('data-shopify-editor-block');
   track.append(primary, duplicate); gallery.append(track);
  }
  for (const reel of page.querySelectorAll('.media-reel')) {
   if (reel.querySelector('.reel-set') || designMode) continue;
   const primary = document.createElement('div'); primary.className = 'reel-set reel-set-primary';
   primary.append(...reel.children);
   const duplicate = primary.cloneNode(true); duplicate.className = 'reel-set reel-set-duplicate';
   duplicate.setAttribute('aria-hidden', 'true'); duplicate.inert = true;
   for (const video of duplicate.querySelectorAll('video')) { video.removeAttribute('autoplay'); video.pause(); }
   for (const image of duplicate.querySelectorAll('img')) image.alt = '';
   for (const element of duplicate.querySelectorAll('[data-shopify-editor-block]')) element.removeAttribute('data-shopify-editor-block');
   reel.append(primary, duplicate);
  }
  const appearanceObserver = observe(entries => {
   for (const entry of entries) if (entry.isIntersecting) {
    entry.target.classList.add('has-appeared'); appearanceObserver.unobserve(entry.target);
   }
  }, { threshold: 0, rootMargin: '0px 0px -48px' });
  const sections = [...page.querySelectorAll('[data-pm-sp-section]:not(.hero)')];
  const revealSelector = 'h2,h3,.section-index,.origin-date,.profile-logo,p,figure,.cta,.tabs,.purpose-gallery,.media-reel,.press-logo,blockquote footer';
  for (const section of sections) {
   const candidates = [...section.querySelectorAll(revealSelector)].filter(element =>
    !element.closest('[aria-hidden="true"],.ring-panel') &&
    !element.parentElement.closest('figure,.purpose-gallery,.media-reel') &&
    !element.classList.contains('video-status')
   );
   candidates.forEach((element, index) => {
    element.classList.add('appearance-item');
    element.style.setProperty('--appearance-delay', `${(index % 3) * 70}ms`);
    if (element.matches('figure')) element.classList.add('appearance-image');
    if (designMode || reducedMotion.matches) element.classList.add('has-appeared');
    else appearanceObserver.observe(element);
   });
  }
  page.addEventListener('focusin', event => event.target.closest('.appearance-item')?.classList.add('has-appeared'), { signal });
  const loops = [...page.querySelectorAll('.purpose,.films-section,.ring-section')];
  const loopTimers = new Map(); const loopVisibility = new Map();
  function updateLoop(section, visible) {
   clearTimeout(loopTimers.get(section)); section.classList.remove('is-looping');
   if (!visible || reducedMotion.matches || designMode || document.hidden) return;
   const delay = section.matches('.films-section') ? Number(section.dataset.autoplayDelay ?? 4000) : 0;
   loopTimers.set(section, setTimeout(() => section.classList.add('is-looping'), delay));
  }
  const loopObserver = observe(entries => {
   for (const entry of entries) { loopVisibility.set(entry.target, entry.isIntersecting); updateLoop(entry.target, entry.isIntersecting); }
  }, { threshold: .08 });
  for (const loop of loops) loopObserver.observe(loop);
  const refreshPlayback = () => {
   for (const loop of loops) updateLoop(loop, loopVisibility.get(loop));
   for (const video of videos) playVideo(video, videoVisibility.get(video));
   refreshHeroGrain();
   if (reducedMotion.matches) page.querySelectorAll('.appearance-item').forEach(item => item.classList.add('has-appeared'));
  };
  document.addEventListener('visibilitychange', refreshPlayback, { signal });
  reducedMotion.addEventListener('change', refreshPlayback, { signal });
  cleanups.push(() => { for (const timer of loopTimers.values()) clearTimeout(timer); });
  const destroy = () => { controller.abort(); for (const cleanup of cleanups) cleanup(); };
  controllers.set(page, destroy);
 }
 function initializePages() { document.querySelectorAll('.pm-sp-page').forEach(initialize); }
 if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializePages, { once: true });
 else initializePages();
 document.addEventListener('shopify:section:load', initializePages);
 document.addEventListener('shopify:section:unload', event => {
  const page = event.target.closest('.pm-sp-page');
  if (page) controllers.get(page)?.();
  requestAnimationFrame(initializePages);
 });
 document.addEventListener('shopify:block:select', event => {
  event.target.closest('.pm-sp-page')?.querySelectorAll('.appearance-item').forEach(item => item.classList.add('has-appeared'));
 });
})();
